import type { ReactNode } from "react";
import type { ControllerRenderProps } from "react-hook-form";
import styled from "styled-components";
import workPolicyApplicationApi from "../../../../api/work-policy-application-api";
import type { WorkPolicyApplicationApi } from "../../../../api/work-policy-application-api.types";
import StyleInput from "../../../inputs/style-input";
import StyleTextarea from "../../../inputs/style-textarea";
import { Content, Label, Subscription } from "../styles";
import type { IApprovalFormFieldValue } from "../types";
import { EligiblePolicySelect } from "./eligible-policy-select";
import { WorkPolicyApplicationDetail } from "./work-policy-application-form";
import { WorkPolicyApplicationMetadataFields } from "./work-policy-application-metadata-fields";
import { useWorkPolicyApplicationFormModel } from "./use-work-policy-application-form-model";
import type { WorkPolicyApplicationController } from "./use-work-policy-application-controller";

interface WorkPolicyApprovalRequestFormProps {
  basicInfo: ReactNode;
  templateTitle: string;
  startDate: ControllerRenderProps<IApprovalFormFieldValue, "startDate">;
  endDate: ControllerRenderProps<IApprovalFormFieldValue, "endDate">;
  title: ControllerRenderProps<IApprovalFormFieldValue, "title">;
  description: ControllerRenderProps<IApprovalFormFieldValue, "description">;
  controller: WorkPolicyApplicationController;
  api?: WorkPolicyApplicationApi;
  readOnly?: boolean;
}

const DetailSurface = styled.section`
  display: grid;
  width: 100%;
  min-width: 0;
  gap: 12px;
  padding: 14px;

  > h2,
  > h3,
  p {
    margin: 0;
  }
`;

const ReasonField = styled.div`
  display: grid;
  flex: 2;
  min-width: 0;
  gap: 6px;
  padding: 10px;

  textarea {
    min-height: 110px;
    width: 100%;
  }
`;

export function WorkPolicyApprovalRequestForm({
  basicInfo,
  templateTitle,
  startDate,
  endDate,
  title,
  description,
  controller,
  api = workPolicyApplicationApi,
  readOnly = false
}: WorkPolicyApprovalRequestFormProps) {
  const model = useWorkPolicyApplicationFormModel({
    startDate,
    endDate,
    controller,
    api
  });
  return (
    <>
      {basicInfo}
      <WorkPolicyApplicationMetadataFields
        templateTitle={templateTitle}
        startDate={startDate}
        endDate={endDate}
        model={model}
        readOnly={readOnly}
      />
      <Content style={{ gridColumn: "span 3" }}>
        <Label htmlFor="work-policy-id">근무제 유형</Label>
        <Subscription>
          <EligiblePolicySelect
            status={model.eligibleStatus}
            policies={model.policies}
            selectedPolicyId={controller.draft?.workPolicyId ?? null}
            disabled={readOnly}
            onSelect={model.selectPolicy}
            onRetry={model.retryEligible}
          />
        </Subscription>
      </Content>
      <Content style={{ gridColumn: "span 3" }}>
        <Label htmlFor="work-policy-title">제목</Label>
        <Subscription>
          <StyleInput
            {...title}
            id="work-policy-title"
            minWidth="300px"
            maxWidth="100%"
            $width="100%"
            disabled={readOnly}
          />
        </Subscription>
      </Content>
      <Content style={{ gridColumn: "span 3" }}>
        <DetailSurface aria-labelledby="work-policy-detail-title">
          <h2 id="work-policy-detail-title">신청 상세</h2>
          <WorkPolicyApplicationDetail model={model} />
        </DetailSurface>
      </Content>
      <Content style={{ gridColumn: "span 3" }}>
        <Label htmlFor="work-policy-description">신청 사유/비고</Label>
        <ReasonField>
          <StyleTextarea
            {...description}
            id="work-policy-description"
            disabled={readOnly}
          />
          <small>유형별 신청 내용에 덧붙일 사유나 참고 사항을 입력해 주세요.</small>
        </ReasonField>
      </Content>
    </>
  );
}
